import { useMemo } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import type { ThirdPartiesCmp_SomePerson } from '../queries/some-people.query';

import { useFindAllEnabledExternalPeopleQuery } from './useFindAllEnabledExternalPeopleQuery.hook';
import { useFindAllDisabledExternalPeopleQuery } from './useFindAllDisabledExternalPeopleQuery.hook';
import { useFindAllDeletedExternalPeopleQuery } from './useFindAllDeletedExternalPeopleQuery.hook';

interface UnionResult {
  readonly data:
    | Readonly<{ findSomePeople: readonly ThirdPartiesCmp_SomePerson[] }>
    | undefined;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export const useFindAllExternalPeopleQuery = (enabled = true): UnionResult => {
  const deletedQuery = useFindAllDeletedExternalPeopleQuery(enabled);
  const disabledQuery = useFindAllDisabledExternalPeopleQuery(enabled);
  const enabledQuery = useFindAllEnabledExternalPeopleQuery(enabled);

  const memoizedResult = useMemo(() => {
    const deletedData = deletedQuery.data?.findSomePeople;
    const disabledData = disabledQuery.data?.findSomePeople;
    const enabledData = enabledQuery.data?.findSomePeople;

    const data =
      deletedData || disabledData || enabledData
        ? {
            findSomePeople: [
              ...(deletedData ?? EMPTY_ARRAY),
              ...(disabledData ?? EMPTY_ARRAY),
              ...(enabledData ?? EMPTY_ARRAY),
            ],
          }
        : undefined;

    const isLoading =
      deletedQuery.isLoading ||
      disabledQuery.isLoading ||
      enabledQuery.isLoading;
    const isSuccess =
      deletedQuery.isSuccess &&
      disabledQuery.isSuccess &&
      enabledQuery.isSuccess;

    return { data, isLoading, isSuccess };
  }, [
    deletedQuery.data?.findSomePeople,
    deletedQuery.isLoading,
    deletedQuery.isSuccess,
    disabledQuery.data?.findSomePeople,
    disabledQuery.isLoading,
    disabledQuery.isSuccess,
    enabledQuery.data?.findSomePeople,
    enabledQuery.isLoading,
    enabledQuery.isSuccess,
  ]);

  return memoizedResult;
};
